import { Component, Input, OnInit } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { MerchantsService } from '../../core/services/merchants.service';
import { ToastNotificationsService } from '../../core/services/toast-notifications.service';
import { UploadFileComponent } from '../../shared-with-client/upload-file/upload-file.component';
import { LoaderComponent } from '../../shared-with-client/loader/loader.component';
import { DocumentPreviewModalComponent } from '../document-preview-modal/document-preview-modal.component';

@Component({
  selector: 'app-client-attachments',
  templateUrl: './client-attachments.component.html',
  styleUrls: ['./client-attachments.component.scss'],
  imports: [NgClass, DatePipe, TranslatePipe, UploadFileComponent, LoaderComponent],
  standalone: true,
})
export class ClientAttachmentsComponent implements OnInit {
  @Input() client: any;

  public loading = true;
  public saving = false;
  public daysWithAttachments: { createdAt: string; attachments: any[] }[] = [];
  public readonly maxSizeMb = 5;

  constructor(
    private merchantsService: MerchantsService,
    private toastNotificationsService: ToastNotificationsService,
    private translate: TranslateService,
    private modalService: BsModalService,
  ) {}

  ngOnInit() {
    this.loadAttachments();
  }

  loadAttachments() {
    if (!this.client?.id) {
      this.loading = false;
      return;
    }

    this.loading = true;
    this.merchantsService.getClientFiles(this.client.id).subscribe({
      next: (files) => {
        this.daysWithAttachments = this.groupByDay(files || []);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.toastNotificationsService.showToast(
          err?.errMsg || this.translate.instant('GENERAL.generalErrorMsg'),
          'error',
        );
      },
    });
  }

  groupByDay(attachments: any[]) {
    const grouped: { [key: string]: any[] } = attachments.reduce((acc, attachment) => {
      const date = new Date(attachment?.createdAt);
      const day = Number.isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
      if (!acc[day]) {
        acc[day] = [];
      }
      acc[day].push(attachment);
      return acc;
    }, {});

    return Object.keys(grouped)
      .map((day) => {
        const dayAttachments = grouped[day].sort((a, b) => {
          const isAImage = this.isImage(a);
          const isBImage = this.isImage(b);
          if (isAImage && !isBImage) {
            return -1;
          }
          if (!isAImage && isBImage) {
            return 1;
          }
          return 0;
        });
        return {
          createdAt: day,
          attachments: dayAttachments,
        };
      })
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  isImage(attachment: any): boolean {
    const fileType = String(attachment?.fileType || '').toLowerCase();
    if (fileType.startsWith('image/')) {
      return true;
    }

    return /\.(jpe?g|png|gif|webp|svg|bmp|tiff)$/i.test(attachment?.fileName || '');
  }

  isPdf(attachment: any): boolean {
    const fileType = String(attachment?.fileType || '').toLowerCase();
    return fileType === 'application/pdf' || /\.pdf$/i.test(attachment?.fileName || '');
  }

  onUploadComplete({ url, fileName, fileType, fileSize }) {
    if (!url || !this.client?.id) {
      return;
    }

    this.saving = true;
    this.merchantsService.createClientFile(this.client.id, {
      fileUrl: url,
      fileName,
      fileType,
      fileSize,
    }).subscribe({
      next: () => {
        this.saving = false;
        this.loadAttachments();
      },
      error: (err) => {
        this.saving = false;
        this.toastNotificationsService.showToast(
          err?.errMsg || this.translate.instant('GENERAL.generalErrorMsg'),
          'error',
        );
      },
    });
  }

  openFilePreview(attachment: any) {
    const previewModal: BsModalRef = this.modalService.show(DocumentPreviewModalComponent, {
      initialState: {
        attachment,
        client: this.client,
      },
      class: 'second modal-lg',
    });

    const modalCount = this.modalService.getModalsCount();
    const modalCallback: Subscription = this.modalService.onHide.subscribe((reason: string) => {
      if (modalCount - 1 !== this.modalService.getModalsCount()) {
        return;
      }

      modalCallback.unsubscribe();
      if (reason === 'deleted' || previewModal.content?.deleted) {
        this.loadAttachments();
      }
    });
  }
}
